import React, { useState, useMemo, useEffect, useCallback } from 'react';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import type { StatCardData } from '../types';
import { fetchSubscriptions, activateSubscription, deactivateSubscription, type AdminSubscription } from '../lib/subscriptionService';
import { logger } from '../lib/logger';

const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border border-blue-200',
  past_due: 'bg-red-50 text-red-700 border border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border border-gray-200',
  expired: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const statusLabels: Record<string, string> = {
  active: 'Actif',
  past_due: 'Impayé',
  cancelled: 'Résilié',
  expired: 'Expiré',
};

const Subscriptions: React.FC = () => {
  const [subs, setSubs] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const reload = useCallback(() => {
    return fetchSubscriptions()
      .then(setSubs)
      .catch((err) => logger.error('load_subscriptions', 'Subscriptions', { error: String(err) }));
  }, []);

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  const premiumSubs = subs.filter((s) => s.plan === 'premium');
  const activePremium = premiumSubs.filter((s) => s.status === 'active');
  const freeSubs = subs.filter((s) => s.plan === 'free');
  const mrr = activePremium.reduce((sum, s) => sum + (s.amountHt || 0), 0);

  const subStats: StatCardData[] = [
    { label: 'Comptes gratuits', value: String(freeSubs.length), icon: '🆓', color: 'blue', subtitle: 'recruteurs en modèle publicitaire' },
    { label: 'MRR', value: `${mrr.toLocaleString('fr-FR')} €`, icon: '💰', color: 'green', subtitle: `${activePremium.length} abonnement(s) payant(s)` },
    { label: 'Impayés', value: String(premiumSubs.filter(s => s.status === 'past_due').length), icon: '⚠', color: 'red', subtitle: 'à relancer' },
  ];

  const filtered = useMemo(() => {
    return subs.filter((s) => {
      const matchSearch = search === '' || s.companyName.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || s.status === statusFilter;
      const matchPlan = planFilter === 'all' || s.plan === planFilter;
      return matchSearch && matchStatus && matchPlan;
    });
  }, [subs, search, statusFilter, planFilter]);

  const handleActivate = async (s: AdminSubscription) => {
    setActionLoading(s.id);
    try {
      await activateSubscription(s.employerId);
      await reload();
      alert('Abonnement payant activé manuellement.');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (s: AdminSubscription) => {
    setActionLoading(s.id);
    try {
      await deactivateSubscription(s.employerId);
      await reload();
      alert('Abonnement résilié, le compte repasse en gratuit.');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: Column<AdminSubscription>[] = [
    {
      key: 'company',
      header: 'Entreprise',
      render: (s) => <span className="font-medium text-gray-900">{s.companyName || '—'}</span>,
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (s) => (
        <span
          className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${
            s.plan === 'premium'
              ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200'
          }`}
        >
          {s.plan === 'premium' ? '★ Premium' : 'Gratuit'}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Montant',
      render: (s) => <span className="font-semibold text-gray-900">{s.amountHt || 0} €<span className="text-gray-400 font-normal">/mois</span></span>,
    },
    {
      key: 'status',
      header: 'Statut',
      render: (s) => (
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${statusClasses[s.status] || 'bg-gray-100 text-gray-500'}`}>
          {statusLabels[s.status] || s.status}
        </span>
      ),
    },
    {
      key: 'period',
      header: 'Période',
      render: (s) => (
        <div className="text-xs text-gray-500">
          <p>{s.currentPeriodStart || '—'}</p>
          <p>→ {s.currentPeriodEnd || '—'}</p>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Créé le',
      render: (s) => <span className="text-gray-500 text-xs">{s.createdAt}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (s) => (
        <div className="flex items-center gap-2">
          {!(s.plan === 'premium' && s.status === 'active') && (
            <button
              onClick={() => handleActivate(s)}
              disabled={actionLoading === s.id}
              className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-40"
            >
              Passer en premium
            </button>
          )}
          {s.plan === 'premium' && s.status === 'active' && (
            <button
              onClick={() => handleDeactivate(s)}
              disabled={actionLoading === s.id}
              className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40"
            >
              Résilier
            </button>
          )}
        </div>
      ),
    },
  ];

  const selectClass = 'px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]';

  const filterSlot = (
    <div className="flex items-center gap-2">
      <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className={selectClass}>
        <option value="all">Tous les plans</option>
        <option value="free">Gratuit</option>
        <option value="premium">Premium</option>
      </select>
      <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
        <option value="all">Tous les statuts</option>
        <option value="active">Actif</option>
        <option value="past_due">Impayé</option>
        <option value="cancelled">Résilié</option>
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {subStats.map((stat, i) => (
          <StatCard key={i} data={stat} />
        ))}
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(s) => s.id}
        searchable
        searchPlaceholder="Rechercher par entreprise..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
      />
    </div>
  );
};

export default Subscriptions;
